import * as React from "react";
import dayjs from "dayjs";
import { CalendarIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
// useTranslation is still needed for the X clear button's aria-label.

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/*
 * Single-date picker built on shadcn Calendar + Popover. Replaces MUI X
 * DatePicker for the migrated pages; the API matches what the existing
 * consumers already pass (Day.js value + onChange).
 *
 * The trigger is a Button styled like an Input. The X clear button is a
 * sibling of the Popover (absolute-positioned over the trigger's right
 * edge) — nesting it inside the trigger would render a <button> inside a
 * <button>, which is invalid HTML and causes Radix to treat clicks on the
 * X as a trigger click, swallowing the clear gesture.
 */

interface DatePickerProps {
  value?: dayjs.Dayjs | null;
  onChange: (next: dayjs.Dayjs | null) => void;
  label?: React.ReactNode;
  format?: string;
  locale?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  label,
  format = "DD/MM/YYYY",
  locale,
  placeholder,
  disabled,
  className,
}: DatePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const display =
    value && value.isValid()
      ? (locale ? value.locale(locale) : value).format(format)
      : "";

  const selectedDate = value && value.isValid() ? value.toDate() : undefined;

  function handleSelect(next: Date | undefined) {
    onChange(next ? dayjs(next) : null);
    setOpen(false);
  }

  return (
    <div className={cn("space-y-1", className)}>
      {label ? (
        <label className="text-sm font-medium">{label}</label>
      ) : null}
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start gap-2 font-normal",
                value && "pr-10",
                !value && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="flex-1 text-left">
                {display || placeholder || ""}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleSelect}
              defaultMonth={selectedDate}
              captionLayout="dropdown"
              startMonth={new Date(1970, 0)}
              endMonth={new Date(new Date().getFullYear() + 10, 11)}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        {value ? (
          <button
            type="button"
            onMouseDown={(e) => {
              // Stop the click from reaching the trigger button beneath
              // and prevent Radix from re-opening on the same gesture.
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
            }}
            aria-label={t("common.clear", "Clear")}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
